import React from 'react';
import { graphql, useFragment } from 'utils/graphClient';
import { OptionsMenuItem } from 'components/OptionsMenu/OptionsMenuItem';
import { EditPencilIcon } from 'components/icons';
import { t } from '@lingui/macro';
import { useDisclosure } from '@cardboard-ui/react';
import { EditOption_node$key } from './__generated__/EditOption_node.graphql';
import { useOpenItemViewer } from 'apps/TenantApp/screens/ItemViewer/useOpenItemViewer';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { createIcon } from 'components/icons/createIcon';
import { ITEM_VIEWER_EDIT_MODE_QUERY_PARAM_NAME } from 'utils/routes';
import { EditAnnouncementOverlay } from 'apps/TenantApp/screens/Space/screens/Activity/components/Announcements/components/EditAnnouncementOverlay';

const ReadModeIcon = createIcon(light('book-open'));

const nodeFragment = graphql`
  fragment EditOption_node on Node {
    __typename
    id
    ... on Announcement {
      canEdit: canUpdate {
        value
      }
    }
    ... on Event {
      canEdit: canUpdate {
        value
      }
    }
    ... on FolderItem {
      canEdit: canRename {
        value
      }
      content {
        __typename
      }
    }
  }
`;

interface EditOptionProps {
  node: EditOption_node$key;
}

export const EditOption = ({ node }: EditOptionProps) => {
  const { __typename, id, canEdit, content } = useFragment(nodeFragment, node);

  if (!canEdit?.value) return null;

  switch (__typename) {
    case 'Announcement':
      return <EditAnnouncementOption announcementId={id} />;
    case 'FolderItem':
      return ['Page', 'Form'].includes(content?.__typename || '') ? (
        <EditFolderItemOption folderItemId={id} />
      ) : null;
    case 'Folder':
    case 'Vault':
    case 'VaultItem':
    case 'Event':
    default:
      return null;
  }
};

const EditFolderItemOption = ({ folderItemId }: { folderItemId: string }) => {
  const {
    openItemViewer,
    isOpen,
    itemId: currentlyOpenItemId,
    startEditMode,
    stopEditMode,
    isEditModeEnabled,
  } = useOpenItemViewer();
  if (currentlyOpenItemId !== folderItemId) {
    return (
      <OptionsMenuItem
        icon={<EditPencilIcon />}
        onClick={() => {
          openItemViewer(folderItemId, {
            [ITEM_VIEWER_EDIT_MODE_QUERY_PARAM_NAME]: 'true',
          });
        }}
      >
        {t`Edit`}
      </OptionsMenuItem>
    );
  }

  if (!isEditModeEnabled) {
    return (
      <OptionsMenuItem
        icon={<EditPencilIcon />}
        onClick={() => {
          startEditMode();
        }}
      >
        {t`Edit`}
      </OptionsMenuItem>
    );
  }

  return (
    <OptionsMenuItem
      icon={<ReadModeIcon />}
      onClick={() => {
        stopEditMode();
      }}
    >
      {t`Read mode`}
    </OptionsMenuItem>
  );
};

const EditAnnouncementOption = ({
  announcementId,
}: {
  announcementId: string;
}) => {
  const {
    isOpen: isEditAnnouncementOpen,
    onOpen: onOpenAnnouncementEdit,
    onClose: onCloseAnnouncementEdit,
  } = useDisclosure();

  return (
    <>
      <OptionsMenuItem
        icon={<EditPencilIcon />}
        onClick={onOpenAnnouncementEdit}
      >
        {t`Edit`}
      </OptionsMenuItem>

      {isEditAnnouncementOpen && (
        <EditAnnouncementOverlay
          announcementId={announcementId}
          isOpen={isEditAnnouncementOpen}
          onClose={onCloseAnnouncementEdit}
        />
      )}
    </>
  );
};
